import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import ProtectedRoute from './components/ProtectedRoute';
import PublicOnlyRoute from './components/PublicOnlyRoute';
import UserRoute from './components/UserRoute';
import { AuthProvider } from './AuthContext';
import { NotificationProvider } from './components/NotificationContext';
import { AddressProvider } from './components/AddressContext';
import ScrollToTop from './components/ScrollToTop';
import { SuccessModalProvider } from './components/SuccessModalContext';
import { API_URL } from './apiConfig';
import HomePage from './components/HomePage';
import LoginForm from './components/LoginForm';
import AdminLoginForm from './components/AdminLoginForm';
import SignupForm from './components/SignupForm';
import HomePageContent from './components/HomePageContent';
import AdminForgotPassword from './components/AdminForgotPassword';
import AdminForgotPasswordVerification from './components/AdminForgotPasswordVerification';
import AdminResetPassword from './components/AdminResetPassword';
import { Suspense, lazy } from 'react';
const ShopPage = lazy(() => import('./components/ShopPage'));
const GalleryPage = lazy(() => import('./components/GalleryPage'));
const AboutUsPage = lazy(() => import('./components/AboutUsPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const HelpCentre = lazy(() => import('./components/HelpCentre'));
const VirtualShowroomPage = lazy(() => import('./components/DesignYourOwnPage'));
const LearnMoreAppPage = lazy(() => import('./components/LearnMoreAppPage'));
const Profile = lazy(() => import('./components/Profile'));
const AddressLayout = lazy(() => import('./components/AddressLayout'));
const AddCartPage = lazy(() => import('./components/AddCartPage'));
const ProductDetails = lazy(() => import('./components/ProductDetails'));
const NotificationSettingsPage = lazy(() => import('./components/NotificationSettingsPage'));
const MyPurchasePage = lazy(() => import('./components/MyPurchasePage'));
const NotificationsPage = lazy(() => import('./components/NotificationsPage'));
const BuyPage = lazy(() => import('./components/BuyPage'));
const TrackOrderPage = lazy(() => import('./components/TrackOrderPage'));
const SearchResults = lazy(() => import('./components/SearchResults'));
const ProductListPage = lazy(() => import('./components/ProductListPage'));
const CustomFurnitureRequest = lazy(() => import('./components/CustomFurnitureRequest'));
const TrackCustomizeRequest = lazy(() => import('./components/TrackCustomizeRequest'));
const CustomizeCheckout = lazy(() => import('./components/CustomizeCheckout'));
const VerifyPage = lazy(() => import('./components/VerifyPage'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ForgotPasswordVerification = lazy(() => import('./components/ForgotPasswordVerification'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ChairPage = lazy(() => import('./components/ChairPage'));
const SofaPage = lazy(() => import('./components/SofaPage'));
const QueenSizeBedPage = lazy(() => import('./components/QueenSizeBedPage'));
const DoorPage = lazy(() => import('./components/DoorPage'));
const DrawerPage = lazy(() => import('./components/DrawerPage'));
const OfficeTablePage = lazy(() => import('./components/OfficeTablePage'));
const HighChairPage = lazy(() => import('./components/HighChairPage'));
const DiningSetPage = lazy(() => import('./components/DiningSetPage'));
const TermofSalePage = lazy(() => import('./components/TermofSalePage'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ToReviewPage = lazy(() => import('./components/ToReviewPage'));

const ProductionDashboard = lazy(() => import('./components/ProductionDashboard'));
const DeliveryDashboard = lazy(() => import('./components/DeliveryDashboard'));

const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes total inactivity (5 min grace period + 15 min session countdown)
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // silently refresh token every 10 min if active
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const getStoredUserRole = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsed = JSON.parse(userStr);
      return (parsed?.role || '').toLowerCase();
    }
  } catch (e) {}
  return (localStorage.getItem('userRole') || '').toLowerCase();
};

const isAdminRoute = () => {
  const path = window.location.pathname;
  const role = getStoredUserRole();
  return path.startsWith('/admin') || role === 'admin';
};

const SessionTimerPill = () => {
  const [secondsLeft, setSecondsLeft] = React.useState(20 * 60);

  React.useEffect(() => {
    let lastResetTime = Date.now();

    const resetTimer = () => {
      lastResetTime = Date.now();
      setSecondsLeft(20 * 60);
    };

    const interval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - lastResetTime) / 1000);
      const remaining = Math.max(0, (20 * 60) - elapsedSeconds);
      setSecondsLeft(remaining);
    }, 1000);

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));

    return () => {
      clearInterval(interval);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, []);

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const timeStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

  const isLow = secondsLeft <= 300;
  const isCritical = secondsLeft <= 60;

  const badgeBg = isCritical ? '#FEE2E2' : isLow ? '#FEF3C7' : '#E6F4EA';
  const textColor = isCritical ? '#DC2626' : isLow ? '#D97706' : '#14291F';
  const borderColor = isCritical ? '#FCA5A5' : isLow ? '#FCD34D' : '#A7F3D0';

  return (
    <div
      title="Idle Session Timeout — Resets automatically whenever you move your mouse, click, or type."
      style={{
        position: 'fixed',
        top: '18px',
        right: '180px',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 12px',
        borderRadius: '20px',
        backgroundColor: badgeBg,
        border: `1.5px solid ${borderColor}`,
        color: textColor,
        fontSize: '12px',
        fontWeight: '700',
        fontFamily: 'monospace',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        userSelect: 'none',
        transition: 'all 0.3s ease',
        cursor: 'help'
      }}
    >
      <span style={{
        display: 'inline-block',
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        backgroundColor: textColor
      }} />
      <span>Idle: {timeStr}</span>
    </div>
  );
};

function App() {
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const sessionExpiredRef = React.useRef(false); // guard to prevent double-trigger

  // --- Idle detection + silent token refresh ---
  React.useEffect(() => {
    let idleTimer = null;
    let refreshTimer = null;

    // Silently refresh the access token using the stored refresh token
    const silentRefresh = async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return;
      try {
        const res = await fetch(`${API_URL}/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.token) {
            localStorage.setItem('token', data.token);
          }
        }
      } catch (e) {
        // Network error — ignore, let the idle timer handle eventual logout
      }
    };

    // Silently refresh the access token on user activity (throttled: at most once per 4 minutes)
    // This ensures the token never expires as long as the admin is actively working,
    // regardless of when the fixed interval last ran.
    const THROTTLE_MS = 4 * 60 * 1000;
    let lastActivityRefresh = 0;

    // Reset the idle countdown on any user activity
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // Only kick admin-role sessions out on idle; regular users stay logged in
        if (isAdminRoute() && localStorage.getItem('token')) {
          if (!sessionExpiredRef.current) {
            sessionExpiredRef.current = true;
            // // setSessionExpired(true); // DISABLED FOR NOW // DISABLED FOR NOW
          }
        }
      }, IDLE_TIMEOUT_MS);

      // Track last activity across tabs to prevent staying logged in if closed
      if (isAdminRoute()) {
        localStorage.setItem('adminLastActivity', Date.now().toString());
      }

      // Also silently refresh token on activity (throttled) so the 15-min
      // access token never expires while the admin is actively using the system
      if (isAdminRoute() && Date.now() - lastActivityRefresh > THROTTLE_MS) {
        lastActivityRefresh = Date.now();
        silentRefresh();
      }
    };

    // Start the refresh loop
    const startRefreshLoop = () => {
      clearInterval(refreshTimer);
      refreshTimer = setInterval(silentRefresh, REFRESH_INTERVAL_MS);
    };

    // Listen for real user activity
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));

    // Also listen for the API-level 401 "auth expired" (e.g. user came back after long break)
    // Only show for admin routes — regular user 401s should NOT trigger this modal
    const handleAuthExpired = () => {
      if (isAdminRoute() && localStorage.getItem('token') && !sessionExpiredRef.current) {
        sessionExpiredRef.current = true;
        setSessionExpired(true);
      }
    };
    window.addEventListener('auth-expired', handleAuthExpired);

    // Initial check: if they closed the tab but >20 mins passed, boot them immediately
    if (isAdminRoute() && localStorage.getItem('token')) {
      const lastAct = parseInt(localStorage.getItem('adminLastActivity') || '0', 10);
      if (false && lastAct > 0 && Date.now() - lastAct > IDLE_TIMEOUT_MS) {
        handleSessionExpiredClose();
        return; // Don't start timers
      }
    }

    // Kick everything off
    resetIdleTimer();
    startRefreshLoop();

    return () => {
      clearTimeout(idleTimer);
      clearInterval(refreshTimer);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  const handleSessionExpiredClose = () => {
    sessionExpiredRef.current = false; // reset the guard
    setSessionExpired(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('adminLastActivity');
    window.location.href = '/login';
  };

  return (
    <AuthProvider>
      <NotificationProvider>
        <SuccessModalProvider>
        {/* Session Expired Modal — dark green design, admin-only */}
        {sessionExpired && (
          <div style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              backgroundColor: '#14291F',
              borderRadius: '16px',
              padding: '32px 28px 28px',
              width: '90%',
              maxWidth: '400px',
              position: 'relative',
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
              color: '#fff',
            }}>
              {/* X close button */}
              <button
                onClick={handleSessionExpiredClose}
                style={{
                  position: 'absolute', top: '14px', right: '16px',
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
                  fontSize: '20px', cursor: 'pointer', lineHeight: 1,
                }}
              >✕</button>

              <h2 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: '700', color: '#fff' }}>
                Session Expired
              </h2>
              <p style={{ margin: '0 0 28px', fontSize: '14px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.6' }}>
                Your session has expired or the token is invalid. Please log in again to continue.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSessionExpiredClose}
                  style={{
                    backgroundColor: '#4ECDC4',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '30px',
                    padding: '10px 32px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >OK</button>
              </div>
            </div>
          </div>
        )}
        <Router>
          <ScrollToTop />
        <Suspense fallback={<div style={{ display: 'flex', height: '100vh', width: '100vw', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
        <Routes>
          {/* Main Public & User Routes */}
          <Route path="/" element={<UserRoute><AddressProvider><HomePage /></AddressProvider></UserRoute>}>
            <Route index element={<HomePageContent />} />
            <Route path="home" element={<HomePageContent />} />
            <Route path="shop" element={<ShopPage />} />
            <Route path="gallery" element={<GalleryPage />} />
            <Route path="about" element={<AboutUsPage />} />
            <Route path="learn-more" element={<LearnMoreAppPage />} />
            <Route path="termofsale" element={<TermofSalePage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="help" element={<HelpCentre />} />
              <Route path="help-centre" element={<HelpCentre />} />
            <Route path="design" element={<VirtualShowroomPage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="cart" element={<AddCartPage />} />
            <Route path="address" element={<AddressProvider><AddressLayout /></AddressProvider>} />
            <Route path="notification-settings" element={<NotificationSettingsPage />} />
            <Route path="my-purchase" element={<MyPurchasePage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="products" element={<ProductListPage />} />
            <Route path="product/:id" element={<ProductDetails />} />
            <Route path="buy" element={<AddressProvider><BuyPage /></AddressProvider>} />
             <Route path="track-orders" element={<TrackOrderPage />} />
             <Route path="track-customize" element={<TrackCustomizeRequest />} />
             <Route path="customize" element={<AddressProvider><CustomFurnitureRequest /></AddressProvider>} />
             <Route path="search" element={<SearchResults />} />
             <Route path="chair" element={<ChairPage />} />
             <Route path="sofa" element={<SofaPage />} />
             <Route path="queensizebed" element={<QueenSizeBedPage />} />
             <Route path="door" element={<DoorPage />} />
             <Route path="drawer" element={<DrawerPage />} />
             <Route path="officetable" element={<OfficeTablePage />} />
             <Route path="highchair" element={<HighChairPage />} />
             <Route path="diningset" element={<DiningSetPage />} />
             <Route path="orders/to-review" element={<ToReviewPage />} />

            
            {/* PayMongo Returns */}
            <Route path="payment-success" element={<HomePageContent paymentStatus="success" />} />
            <Route path="payment-cancelled" element={<HomePageContent paymentStatus="cancelled" />} />
          </Route>

          <Route path="/login" element={
            <PublicOnlyRoute>
              <LoginForm />
            </PublicOnlyRoute>
          } />
          <Route path="/admin-login" element={
            <PublicOnlyRoute>
              <AdminLoginForm />
            </PublicOnlyRoute>
          } />
          <Route path="/signup" element={
            <PublicOnlyRoute>
              <SignupForm />
            </PublicOnlyRoute>
          } />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/forgot-password-verify" element={<ForgotPasswordVerification />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/forgot-password-verify" element={<AdminForgotPasswordVerification />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />

          {/* Standalone Checkout Pages */}
          <Route path="/customize-checkout/:id" element={
            <UserRoute>
              <AddressProvider>
                <CustomizeCheckout />
              </AddressProvider>
            </UserRoute>
          } />

          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production"
            element={
              <ProtectedRoute adminOnly={true}>
                <ProductionDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery"
            element={
              <ProtectedRoute adminOnly={true}>
                <DeliveryDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
      </Router>
      </SuccessModalProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;

